import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UserSignalrService } from '@app/shared/layout/change-status/user-signalr.service';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BillingApiService, errorMessageOf } from '../billing-api.service';
import { BillingStateService } from '../billing-state.service';
import { BillingStatus } from '../billing.models';

interface BannerView {
    kind: 'none' | 'paymentFailed' | 'cancelPending';
    lead: string;
    rest: string;
}

/**
 * 1e "Billing banners (admins only, top of every page)": red "Payment failed. Update your card by …" during the 7-day retry
 * period (Update card opens the Stripe billing portal) and yellow "Your plan ends on …" while a cancellation is pending (Keep
 * my plan undoes it). Team members see nothing. Also turns a team member's "Ask admin to upgrade" into a web alert for the
 * owner when Teams could not deliver it.
 */
@Component({
    selector: 'vf-billing-banner',
    templateUrl: './billing-banner.component.html',
    styleUrls: ['./billing-banner.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingBannerComponent implements OnInit, OnDestroy {
    view: BannerView = { kind: 'none', lead: '', rest: '' };
    busy = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private api: BillingApiService,
        private billingState: BillingStateService,
        private signalr: UserSignalrService,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.billingState.status$.pipe(takeUntil(this.destroy$)).subscribe(status => this.render(status));
        this.signalr.billingUpgradeRequests.pipe(takeUntil(this.destroy$)).subscribe(request => {
            this.toastr.info(request.message, 'Upgrade request', AppConsts.toastrOption);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    updateCard(): void {
        if (this.busy) {
            return;
        }
        this.busy = true;
        this.cd.markForCheck();
        this.api
            .createBillingPortalSession()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: url => {
                    this.busy = false;
                    this.cd.markForCheck();
                    this.billingState.openStripePage(url);
                },
                error: () => {
                    this.busy = false;
                    this.cd.markForCheck();
                },
            });
    }

    keepPlan(): void {
        if (this.busy) {
            return;
        }
        this.busy = true;
        this.cd.markForCheck();
        this.api
            .keepPlan()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.busy = false;
                    this.toastr.success('Your plan will keep renewing.', null, AppConsts.toastrOption);
                    this.billingState.refresh();
                },
                error: (error: unknown) => {
                    this.busy = false;
                    this.toastr.error(errorMessageOf(error, 'Your plan could not be kept. Please try again.'), null, AppConsts.toastrOption);
                    this.cd.markForCheck();
                },
            });
    }

    private render(status: BillingStatus | null): void {
        const dateOf = (value: string | null, withYear: boolean): string =>
            value
                ? new Date(value).toLocaleDateString('en-US', withYear
                    ? { month: 'short', day: 'numeric', year: 'numeric' }
                    : { month: 'short', day: 'numeric' })
                : '';

        if (!status || !status.isBillingAdmin) {
            this.view = { kind: 'none', lead: '', rest: '' };
        } else if (status.state === 'paymentFailed') {
            this.view = {
                kind: 'paymentFailed',
                lead: 'Payment failed.',
                rest: ` Update your card by ${dateOf(status.paymentDueBy, false)} to keep using Trovve.`,
            };
        } else if (status.state === 'cancelPending') {
            this.view = {
                kind: 'cancelPending',
                lead: `Your plan ends on ${dateOf(status.periodEnd, true)}.`,
                rest: ' After that, your team can’t use Trovve.',
            };
        } else {
            this.view = { kind: 'none', lead: '', rest: '' };
        }
        this.cd.markForCheck();
    }
}
